import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Grid,
  Skeleton,
  IconButton,
  Typography,
  Button,
  Box,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getLocalChapterIRSFormDetail,
  getSPDIRSFormDetail,
  getReciprocityIRSFormDetail,
  updateLocalChapterIRSForm,
  updateSPDIRSForm,
  updateReciprocityIRSForm,
} from '../../../endpoints/irsForms';
import { UpdateIRSFormRequest } from '../../../types/irsForms';
import IRSFormSections from './IRSFormSections';
import PageHeader from '../../../Components/PageHeader';
import AlertItem from '../../../Components/AlertItem';
import UserSearchModal from '../../../Components/UserSearchModal';
import { applications } from '../../../utils/variables';

// First status IDs for each IRS form type
const IRS_FIRST_STATUS_IDS = {
  lc: 'a21f7257bb00430da43f57a9d61baa90', // Local Chapter Treasurer
  sc: 'f0249abe83b441b5ac536123065b46c0', // S/P/D Treasurer
  rg: 'd2c5f29fb08a4282ad4bad6cc4cb6bb2', // Reciprocity Treasurer
} as const;

// Status names for display in messages
const IRS_FIRST_STATUS_NAMES = {
  lc: 'Local Chapter Treasurer',
  sc: 'S/P/D Treasurer',
  rg: 'Reciprocity Treasurer',
} as const;

// Helper to check if status is first status
const isFirstStatus = (
  statusId: string | undefined,
  formType: 'lc' | 'sc' | 'rg'
): boolean => {
  if (!statusId) return false;
  return statusId === IRS_FIRST_STATUS_IDS[formType];
};

const IRSFormsDetail: React.FC = () => {
  const { id, year } = useParams<{
    id: string;
    year: string;
  }>();
  const location = useLocation();

  const [hideContent, setHideContent] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('success');
  const [openUserSelect, setOpenUserSelect] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editingStatusId, setEditingStatusId] = useState<string>('');
  const [editingAssigneeId, setEditingAssigneeId] = useState<string>('');
  const [editingAssigneeName, setEditingAssigneeName] = useState<string>('');
  const [editingApprovers, setEditingApprovers] = useState<
    Record<string, string>
  >({});
  const [editingApproverNames, setEditingApproverNames] = useState<
    Record<string, string>
  >({});
  const [editingApproverStatusId, setEditingApproverStatusId] = useState<
    string | null
  >(null);

  // Determine form type from URL path
  const actualFormType: 'lc' | 'sc' | 'rg' = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/local-chapters/')) {
      return 'lc';
    } else if (path.includes('/spd/')) {
      return 'sc';
    } else if (path.includes('/reciprocity/')) {
      return 'rg';
    }
    return 'lc';
  }, [location.pathname]);

  // Get appropriate detail function based on form type
  const getDetailFunction = () => {
    switch (actualFormType) {
      case 'lc':
        return getLocalChapterIRSFormDetail;
      case 'sc':
        return getSPDIRSFormDetail;
      case 'rg':
        return getReciprocityIRSFormDetail;
      default:
        return getLocalChapterIRSFormDetail;
    }
  };

  // Get appropriate update function based on form type
  const getUpdateFunction = () => {
    switch (actualFormType) {
      case 'lc':
        return updateLocalChapterIRSForm;
      case 'sc':
        return updateSPDIRSForm;
      case 'rg':
        return updateReciprocityIRSForm;
      default:
        return updateLocalChapterIRSForm;
    }
  };

  const { data, isLoading, refetch } = useApiGet(
    ['irs-form-detail', id, actualFormType],
    () => getDetailFunction()(id!),
    {
      enabled: !!id,
      retry: 1,
      refetchInterval: 15 * 60 * 1000, // Refetch every 15 minutes
    }
  );

  const { mutate: updateForm, isPending: isUpdatingForm } = useApiSend(
    (data: UpdateIRSFormRequest) => getUpdateFunction()(id!, data),
    () => {
      setAlertMessage('IRS Form successfully updated.');
      setAlertSeverity('success');
      setShowAlert(true);
      setIsEditing(false);
      refetch();
    },
    (error) => {
      console.error(error);
      setAlertMessage(
        'Error updating IRS Form. If the problem persists, please contact support.'
      );
      setAlertSeverity('error');
      setShowAlert(true);
    }
  );

  // Initialize editing state when data loads
  useEffect(() => {
    if (data && isARAdmin() && !isEditing) {
      setEditingStatusId(data.status_id);
      setEditingAssigneeId(data.assigned_to_id || '');
      setEditingAssigneeName(data.assigned_to || '');
      // Initialize approvers map
      const approversMap: Record<string, string> = {};
      const approverNamesMap: Record<string, string> = {};
      data.approvers.forEach((approver) => {
        approversMap[approver.status_id] = approver.user_id;
        approverNamesMap[approver.status_id] = approver.user_name;
      });
      setEditingApprovers(approversMap);
      setEditingApproverNames(approverNamesMap);
    }
  }, [data, isEditing]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (data) {
      setEditingStatusId(data.status_id);
      setEditingAssigneeId(data.assigned_to_id || '');
      setEditingAssigneeName(data.assigned_to || '');
      const approversMap: Record<string, string> = {};
      const approverNamesMap: Record<string, string> = {};
      data.approvers.forEach((approver) => {
        approversMap[approver.status_id] = approver.user_id;
        approverNamesMap[approver.status_id] = approver.user_name;
      });
      setEditingApprovers(approversMap);
      setEditingApproverNames(approverNamesMap);
    }
    setIsEditing(false);
    setEditingApproverStatusId(null);
  };

  const handleSaveEdit = () => {
    if (!id || !data) return;

    const updateData: UpdateIRSFormRequest = {};

    if (editingStatusId !== data.status_id) {
      updateData.status_id = editingStatusId;
    }

    if (editingAssigneeId !== data.assigned_to_id) {
      updateData.assignee_id = editingAssigneeId || null;
    }

    // Check for approver changes
    const approverUpdates: Array<{
      status_id: string;
      approver_id: string | null;
    }> = [];

    data.approvers.forEach((approver) => {
      const currentApproverId = approver.user_id;
      const newApproverId = editingApprovers[approver.status_id] || null;

      if (currentApproverId !== newApproverId) {
        approverUpdates.push({
          status_id: approver.status_id,
          approver_id: newApproverId,
        });
      }
    });

    if (approverUpdates.length > 0) {
      updateData.approvers = approverUpdates;
    }

    // Only send request if there are changes
    if (Object.keys(updateData).length > 0 || approverUpdates.length > 0) {
      updateForm(updateData);
    } else {
      // No changes, just exit edit mode
      setIsEditing(false);
    }
  };

  const handleSetAssignee = () => {
    setEditingApproverStatusId(null);
    setOpenUserSelect(true);
  };

  const handleSetApprover = (statusId: string) => {
    setEditingApproverStatusId(statusId);
    setOpenUserSelect(true);
  };

  const getAssigneeName = () => {
    if (isEditing && editingAssigneeName) {
      return editingAssigneeName;
    }
    return data?.assigned_to || 'Unassigned';
  };

  const getApproverName = (statusId: string) => {
    if (isEditing) {
      return editingApproverNames[statusId] || 'Unassigned';
    }
    const approver = data?.approvers.find((a) => a.status_id === statusId);
    return approver?.user_name || 'Unassigned';
  };

  const onAssignedUpdate = (user: {
    id: string;
    full_name?: string;
    first_name?: string;
    last_name?: string;
  }) => {
    setOpenUserSelect(false);
    const userId = user.id;
    const userName =
      user.full_name ||
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      userId;

    if (userId === '0') {
      // Unassign
      if (editingApproverStatusId) {
        setEditingApprovers((prev) => {
          const updated = { ...prev };
          delete updated[editingApproverStatusId];
          return updated;
        });
        setEditingApproverNames((prev) => {
          const updated = { ...prev };
          delete updated[editingApproverStatusId];
          return updated;
        });
      } else {
        setEditingAssigneeId('');
        setEditingAssigneeName('');
      }
      setEditingApproverStatusId(null);
      return;
    }

    if (editingApproverStatusId) {
      // Updating approver
      setEditingApprovers((prev) => ({
        ...prev,
        [editingApproverStatusId]: userId,
      }));
      setEditingApproverNames((prev) => ({
        ...prev,
        [editingApproverStatusId]: userName,
      }));
      setEditingApproverStatusId(null);
    } else {
      // Updating assignee
      setEditingAssigneeId(userId);
      setEditingAssigneeName(userName);
    }
  };

  const allowEdit = () => {
    if (!data) return false;
    const isAssigned = data.assigned_to_id === localStorage.getItem('user_id');
    const isFirst = isFirstStatus(data.status_id, actualFormType);
    // Only allow editing if user is assigned AND status is first status
    return isAssigned && isFirst;
  };

  const isARAdmin = () => {
    return (
      localStorage.getItem('apps')?.includes(applications.AnnualReportAdmin) ||
      false
    );
  };

  const getChapterName = () => {
    if (actualFormType === 'sc' && data?.spd_chapter_name) {
      return data.spd_chapter_name;
    }
    if (actualFormType === 'rg' && data?.reciprocity_name) {
      return data.reciprocity_name;
    }
    return data?.local_chapter_name || '';
  };

  const getFormTypeLabel = () => {
    if (actualFormType === 'sc') return 'IRS-SC/PC';
    if (actualFormType === 'rg') return 'IRS-RG';
    return 'IRS-LC';
  };

  const handleApprove = () => {
    refetch();
    setAlertMessage('Form approved successfully.');
    setAlertSeverity('success');
    setShowAlert(true);
  };

  const handleReject = () => {
    refetch();
    setAlertMessage('Form rejected successfully.');
    setAlertSeverity('success');
    setShowAlert(true);
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" height={500} />;
  }

  if (!data) {
    return <Typography>Form not found</Typography>;
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        {showAlert && (
          <AlertItem
            severity={alertSeverity}
            description={alertMessage}
            onClose={() => setShowAlert(false)}
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <PageHeader
          title={`${getChapterName()} - ${getFormTypeLabel()} (${year})`}
          leftDom={null}
          subtitle=""
          rightDom={
            isARAdmin() ? (
              <Button
                variant="outlined"
                onClick={() => setHideContent(!hideContent)}
              >
                {hideContent ? 'Show Info' : 'Hide Info'}
              </Button>
            ) : null
          }
        />
      </Grid>
      {isARAdmin() && !hideContent && (
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12} padding={1}>
              {isEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 250 }}>
                    <InputLabel id="status-select-label">Status</InputLabel>
                    <Select
                      labelId="status-select-label"
                      id="status-select"
                      value={editingStatusId}
                      label="Status"
                      onChange={(e) => setEditingStatusId(e.target.value)}
                    >
                      {/* Status options - using approvers to get status list for now */}
                      {data.approvers.map((approver) => (
                        <MenuItem
                          key={approver.status_id}
                          value={approver.status_id}
                        >
                          {approver.status_name}
                        </MenuItem>
                      ))}
                      {/* Also include current status if not in approvers */}
                      {!data.approvers.find(
                        (a) => a.status_id === data.status_id
                      ) && (
                        <MenuItem value={data.status_id}>
                          {data.status_name}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSetAssignee}
                  >
                    Assignee: {getAssigneeName()}
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="body1">
                      <strong>Status:</strong> {data.status_name}
                    </Typography>
                    <IconButton
                      onClick={handleEditClick}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                  {data.assigned_to && (
                    <Typography variant="body1">
                      <strong>Assigned to:</strong> {data.assigned_to}
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} padding={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Approvers
              </Typography>
              {data.approvers.map((approver) => (
                <Box key={approver.status_id} sx={{ mb: 1.5 }}>
                  {isEditing ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" sx={{ minWidth: 200 }}>
                        {approver.status_name}:
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleSetApprover(approver.status_id)}
                      >
                        {getApproverName(approver.status_id)}
                      </Button>
                    </Box>
                  ) : (
                    <Typography variant="body2">
                      {approver.status_name}:{' '}
                      {getApproverName(approver.status_id)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Paper>
          </Grid>
          {/* Edit Mode Action Buttons */}
          {isEditing && (
            <Grid item xs={12} padding={2}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  disabled={isUpdatingForm}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveEdit}
                  disabled={isUpdatingForm}
                >
                  Save
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
      {!isARAdmin() && !allowEdit() && data && (
        <Grid item xs={12} padding={2}>
          <Alert severity="info">
            {isFirstStatus(data.status_id, actualFormType)
              ? `This form is only editable by the ${IRS_FIRST_STATUS_NAMES[actualFormType]}.`
              : `This form is locked for editing. Only the ${IRS_FIRST_STATUS_NAMES[actualFormType]} can make changes at this stage. You can still sign off when ready.`}
          </Alert>
        </Grid>
      )}
      {data && (
        <Grid item xs={12}>
          <IRSFormSections
            formId={data.id}
            formDetail={data}
            editable={allowEdit()}
            formType={actualFormType}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        </Grid>
      )}
      <UserSearchModal
        modalOpen={openUserSelect}
        setModalOpen={setOpenUserSelect}
        setUser={onAssignedUpdate}
        statusFilter="active"
        userObjectRet={true}
      />
    </Grid>
  );
};

export default IRSFormsDetail;
